// Per-world wildlife cameos — v0.4.8 illustration pass.
// Previous version (v0.4.6-7) used simple ellipse primitives that read as
// "stick figure insects." v0.4.8 redraws each sprite with organic bezier
// silhouettes, layered paper-cut tones (shadow + body + highlight), and
// per-critter secondary animation (wing-flap on flyers, body-wave on the
// glowworm, slow rotation on the leaf). Still Quiet Paper — paper-cut
// illustration quality, not photo-real.
//
// Behavior unchanged from v0.4.6: ambles across the viewport on picker +
// won screens; pauses mid-path for the eye-contact beat (tilt + pulse +
// eye-dots); tap triggers scurry-to-edge + peek-back. Respects
// prefers-reduced-motion.
import { useEffect, useRef, useState } from 'react';

type Direction = 'ltr' | 'rtl';

interface Appearance {
  key: number;
  dir: Direction;
  top: number;
}

interface PeekBack {
  key: number;
  side: 'left' | 'right';
  top: number;
  worldIndex: number;
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduce(m.matches);
    update();
    m.addEventListener('change', update);
    return () => m.removeEventListener('change', update);
  }, []);
  return reduce;
}

interface CameoProps {
  worldIndex: number;
  checkMs?: number;
}

export function WildlifeCameo({ worldIndex, checkMs = 22000 }: CameoProps) {
  const [appearance, setAppearance] = useState<Appearance | null>(null);
  const [peek, setPeek] = useState<PeekBack | null>(null);
  const [scurrying, setScurrying] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  const appearanceRef = useRef(appearance);
  appearanceRef.current = appearance;

  useEffect(() => {
    if (reduceMotion) return;
    const fire = () => {
      if (appearanceRef.current) return;
      setScurrying(false);
      setAppearance({
        key: Date.now(),
        dir: Math.random() < 0.5 ? 'ltr' : 'rtl',
        top: 35 + Math.random() * 45,
      });
    };
    const tick = () => { if (Math.random() < 0.3) fire(); };
    const firstId = window.setTimeout(() => { if (Math.random() < 0.75) fire(); }, 10000 + Math.random() * 12000);
    const id = window.setInterval(tick, checkMs);
    return () => { window.clearInterval(id); window.clearTimeout(firstId); };
  }, [checkMs, reduceMotion, worldIndex]);

  const handleTap = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!appearance || scurrying) return;
    e.stopPropagation();
    setScurrying(true);
  };

  const handleAnimEnd = () => {
    if (scurrying && appearance) {
      const side: 'left' | 'right' = appearance.dir === 'ltr' ? 'right' : 'left';
      setPeek({ key: Date.now(), side, top: appearance.top, worldIndex });
    }
    setAppearance(null);
    setScurrying(false);
  };

  return (
    <>
      {appearance && !reduceMotion && (
        <div
          key={appearance.key}
          className={`cameo cameo-${appearance.dir}${scurrying ? ' cameo-scurry' : ''}`}
          style={{ top: `${appearance.top}%` }}
          onAnimationEnd={handleAnimEnd}
          onClick={handleTap}
          aria-hidden
        >
          <div className="cameo-sprite">
            <CritterSprite worldIndex={worldIndex} />
            <span className="cameo-eyes" aria-hidden>
              <span className="cameo-eye" />
              <span className="cameo-eye" />
            </span>
          </div>
        </div>
      )}
      {peek && !reduceMotion && (
        <div
          key={peek.key}
          className={`cameo-peek cameo-peek-${peek.side}`}
          style={{ top: `${peek.top}%` }}
          onAnimationEnd={() => setPeek(null)}
          aria-hidden
        >
          <CritterSprite worldIndex={peek.worldIndex} />
        </div>
      )}
    </>
  );
}

function CritterSprite({ worldIndex }: { worldIndex: number }) {
  switch (worldIndex) {
    case 1: return <AntSprite />;
    case 2: return <FireflySprite />;
    case 3: return <CrabSprite />;
    case 4: return <BeetleSprite />;
    case 5: return <MothSprite />;
    case 6: return <GlowwormSprite />;
    case 7: return <LeafSprite />;
    default: return <AntSprite />;
  }
}

// ============================================================================
// Shared utilities
// ============================================================================

// Ground shadow — soft ellipse beneath every critter. Grounds them visually
// instead of floating on the accent wash. Uses currentColor at low alpha so
// it inverts with theme.
function GroundShadow({ cx, cy, rx = 8, ry = 1.6 }: { cx: number; cy: number; rx?: number; ry?: number }) {
  return (
    <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="currentColor" opacity="0.15" />
  );
}

// ============================================================================
// Critter sprites — organic bezier paths, layered paper-cut tones
// ============================================================================

function AntSprite() {
  // Three distinct body segments (head/thorax/abdomen) with visible pinches
  // between them, proper insect anatomy. Six jointed legs in a tripod gait
  // pose. Curved antennae.
  return (
    <svg width="44" height="26" viewBox="0 0 44 26" aria-hidden>
      <GroundShadow cx={22} cy={23} rx={14} ry={1.8} />
      {/* Shadow layer — slight offset for paper-cut depth. */}
      <g opacity="0.25" transform="translate(0.8 0.8)">
        <path d="M6 13 Q6 9 10 8 Q14 7 15 11 Q15.2 14 12 15 Q7 15.5 6 13 Z" fill="currentColor" />
        <path d="M16 13 Q16 10 19 10 Q22 10 22 13 Q22 15 19 15 Q16 15 16 13 Z" fill="currentColor" />
        <path d="M23 13 Q23 8 28 7 Q35 7 36 12 Q36 17 30 18 Q24 18 23 13 Z" fill="currentColor" />
      </g>
      {/* Legs — jointed (two-segment) with a visible knee bend. Tripod-
          gait pose: front+back on one side up, middle on other side up. */}
      <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none">
        {/* Left side legs (anchored to thorax area) */}
        <path d="M19 15 L15 20 L13 24" />
        <path d="M19 15 L19 21 L18 25" />
        <path d="M19 15 L23 20 L24 24" />
        {/* Right side legs (mirrored upward) */}
        <path d="M19 11 L15 6 L13 2" />
        <path d="M19 11 L19 5 L18 1" />
        <path d="M19 11 L23 6 L24 2" />
      </g>
      {/* Body — three teardrops with organic curves */}
      <g fill="currentColor">
        {/* Abdomen — largest, teardrop-shaped, pointed rear */}
        <path d="M6 13 Q6 9 10 8 Q14 7 15 11 Q15.2 14 12 15 Q7 15.5 6 13 Z" />
        {/* Thorax — smaller, rounder */}
        <path d="M16 13 Q16 10 19 10 Q22 10 22 13 Q22 15 19 15 Q16 15 16 13 Z" />
        {/* Head — larger, slightly angular, with mandible hint */}
        <path d="M23 13 Q23 8 28 7 Q35 7 36 12 Q36 17 30 18 Q24 18 23 13 Z" />
      </g>
      {/* Mandibles — two tiny forward-pointing stubs at the head's leading edge. */}
      <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none">
        <path d="M36 11 L39 10" />
        <path d="M36 14 L39 15" />
      </g>
      {/* Curved antennae — not straight strokes. Gentle elbow bend partway up. */}
      <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none">
        <path d="M32 8 Q34 5 37 3" />
        <path d="M32 18 Q34 21 37 23" />
      </g>
      {/* Accent eye-dot — tiny. */}
      <circle cx="33" cy="11" r="0.9" fill="var(--accent)" opacity="0.95" />
    </svg>
  );
}

function FireflySprite() {
  // Flight-pose beetle-like bug with a glowing abdomen and actively flapping
  // wings (CSS animation). Four wing sections — two translucent upper wings,
  // flap-animated at different phases for a realistic buzz.
  return (
    <svg width="42" height="28" viewBox="0 0 42 28" aria-hidden>
      <defs>
        <radialGradient id="ff-glow" cx="78%" cy="50%" r="55%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.85" />
          <stop offset="45%" stopColor="var(--accent)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ff-wing-tint" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="var(--ivory)" stopOpacity="0.85" />
          <stop offset="100%" stopColor="var(--ivory)" stopOpacity="0.35" />
        </radialGradient>
      </defs>
      {/* Glow halo around the abdomen */}
      <ellipse cx="30" cy="14" rx="15" ry="10" fill="url(#ff-glow)" />
      {/* Wings — two upper, two lower. Each animates independently.
          transform-origin is at the wing root (body attachment point).
          The SVG transform-origin needs the wing root coordinates. */}
      <g className="ff-wing ff-wing-ul" style={{ transformOrigin: '20px 12px', transformBox: 'view-box' }}>
        <path
          d="M20 12 Q14 4 8 6 Q6 10 11 13 Q16 14 20 12 Z"
          fill="url(#ff-wing-tint)"
          stroke="currentColor"
          strokeOpacity="0.35"
          strokeWidth="0.6"
        />
      </g>
      <g className="ff-wing ff-wing-ur" style={{ transformOrigin: '20px 12px', transformBox: 'view-box' }}>
        <path
          d="M20 12 Q26 4 32 6 Q34 10 29 13 Q24 14 20 12 Z"
          fill="url(#ff-wing-tint)"
          stroke="currentColor"
          strokeOpacity="0.35"
          strokeWidth="0.6"
        />
      </g>
      {/* Body — two connected segments: thorax + abdomen. Abdomen is accent
          color (the "lit up" end); thorax and head are charcoal ink. */}
      <path
        d="M8 14 Q8 10.5 12 10 Q18 10 19 14 Q18 18 12 18 Q8 17.5 8 14 Z"
        fill="currentColor"
      />
      <path
        d="M19 14 Q19 11 24 10.5 Q32 10 34 14 Q32 18 24 17.5 Q19 17 19 14 Z"
        fill="var(--accent)"
      />
      {/* Accent glow speck at the very tip */}
      <circle cx="33" cy="14" r="1.8" fill="var(--ivory)" opacity="0.9" />
      {/* Eye on the head */}
      <circle cx="11" cy="13" r="1.2" fill="currentColor" />
      <circle cx="11.4" cy="12.6" r="0.4" fill="var(--ivory)" />
      {/* Curved antennae */}
      <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none">
        <path d="M8 11 Q5 8 2 7" />
        <path d="M8 15 Q5 16 3 15" />
      </g>
    </svg>
  );
}

function CrabSprite() {
  // Organic crab silhouette. Wider carapace with subtle curve, clearly
  // articulated claws with a visible "pincer gap," eye stalks set further
  // apart, jointed legs. Accent-colored claw tips suggest world identity.
  return (
    <svg width="44" height="32" viewBox="0 0 44 32" aria-hidden>
      <GroundShadow cx={22} cy={29} rx={18} ry={1.8} />
      {/* Legs — 4 per side, each jointed with a visible knee. */}
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none">
        <path d="M10 20 L6 23 L4 28" />
        <path d="M13 21 L10 26 L9 30" />
        <path d="M17 22 L15 27 L14 31" />
        <path d="M27 22 L29 27 L30 31" />
        <path d="M31 21 L34 26 L35 30" />
        <path d="M34 20 L38 23 L40 28" />
      </g>
      {/* Claw arms + pincers. Curved bezier bodies with a pincer gap at tip. */}
      <g fill="currentColor">
        {/* Left arm */}
        <path d="M13 16 Q6 14 3 10 Q1 7 4 5 Q7 4 9 7 Q11 10 14 14 Z" />
        {/* Right arm */}
        <path d="M31 16 Q38 14 41 10 Q43 7 40 5 Q37 4 35 7 Q33 10 30 14 Z" />
      </g>
      {/* Pincer "gap" detail — small ivory slits near tip of each claw. */}
      <g fill="var(--ivory)" opacity="0.55">
        <path d="M4.5 8.5 Q3 8 2.5 7 Q4 6.5 5.5 7.5 Z" />
        <path d="M39.5 8.5 Q41 8 41.5 7 Q40 6.5 38.5 7.5 Z" />
      </g>
      {/* Main body — rounded hexagonal carapace with subtle top flat. */}
      <path
        d="M10 18 Q10 13 16 11 Q22 10 28 11 Q34 13 34 18 Q34 22 28 23 Q22 24 16 23 Q10 22 10 18 Z"
        fill="currentColor"
      />
      {/* Subtle segmentation lines on the carapace */}
      <g stroke="var(--ivory)" strokeWidth="0.7" strokeLinecap="round" strokeOpacity="0.28" fill="none">
        <path d="M16 13 L17 22" />
        <path d="M22 12 L22 23" />
        <path d="M28 13 L27 22" />
      </g>
      {/* Eye stalks with accent pupils — classic crab feature. */}
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none">
        <path d="M18 11 L18 5" />
        <path d="M26 11 L26 5" />
      </g>
      <g>
        <circle cx="18" cy="4" r="2.4" fill="currentColor" />
        <circle cx="26" cy="4" r="2.4" fill="currentColor" />
        <circle cx="18" cy="4" r="1.2" fill="var(--accent)" />
        <circle cx="26" cy="4" r="1.2" fill="var(--accent)" />
        {/* Specular ivory highlights so the eyes have life. */}
        <circle cx="18.5" cy="3.5" r="0.5" fill="var(--ivory)" />
        <circle cx="26.5" cy="3.5" r="0.5" fill="var(--ivory)" />
      </g>
    </svg>
  );
}

function BeetleSprite() {
  // Rounded beetle with a distinct elytra (wing-case) seam, head that
  // projects forward, and accent markings. When the critter "notices you"
  // (CSS pause phase), the wing case briefly opens via an animation on
  // the .beetle-elytra class — a signature beetle move.
  return (
    <svg width="42" height="28" viewBox="0 0 42 28" aria-hidden>
      <GroundShadow cx={21} cy={25} rx={15} ry={1.6} />
      {/* Six jointed legs */}
      <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none">
        <path d="M12 17 L9 22 L7 26" />
        <path d="M20 18 L19 23 L18 27" />
        <path d="M28 17 L31 22 L33 26" />
        <path d="M12 11 L9 6 L7 2" />
        <path d="M20 10 L19 5 L18 1" />
        <path d="M28 11 L31 6 L33 2" />
      </g>
      {/* Body — rounded oval with a subtle rear taper. */}
      <path
        d="M8 14 Q8 9 14 7 Q22 6 30 7 Q34 9 34 14 Q34 19 30 21 Q22 22 14 21 Q8 19 8 14 Z"
        fill="currentColor"
      />
      {/* Elytra seam + accent markings. The seam is offset from center to
          suggest depth; two accent spots per wing case. */}
      <g className="beetle-elytra">
        <path d="M21 8 L21 20" stroke="var(--ivory)" strokeWidth="1.2" strokeOpacity="0.5" />
        <g fill="var(--accent)" opacity="0.9">
          <circle cx="15" cy="11" r="1.4" />
          <circle cx="15" cy="16" r="1.4" />
          <circle cx="27" cy="11" r="1.4" />
          <circle cx="27" cy="16" r="1.4" />
        </g>
      </g>
      {/* Head — projects forward, wider than thorax. */}
      <path
        d="M33 14 Q33 11 36 10 Q40 10 40 14 Q40 18 36 18 Q33 17 33 14 Z"
        fill="currentColor"
      />
      {/* Curved antennae — clubbed tips (typical beetle). */}
      <g>
        <path d="M38 10 Q40 7 41 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <path d="M38 18 Q40 21 41 23" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <circle cx="41" cy="5" r="0.9" fill="currentColor" />
        <circle cx="41" cy="23" r="0.9" fill="currentColor" />
      </g>
      {/* Eye spot */}
      <circle cx="37" cy="13" r="0.7" fill="var(--ivory)" opacity="0.9" />
    </svg>
  );
}

function MothSprite() {
  // Four wings with distinct outer and inner pair — outer pair larger,
  // feathered edges via bezier curves. Moon-spots in ivory. Inner (lower)
  // wings accent-colored. Body is a thin capsule. Feathered antennae.
  // Wings flap independently via CSS.
  return (
    <svg width="48" height="34" viewBox="0 0 48 34" aria-hidden>
      {/* Lower (hind) wings — accent color, rounded. */}
      <g className="moth-wing moth-wing-lower" style={{ transformOrigin: '24px 17px', transformBox: 'view-box' }}>
        <path
          d="M24 17 Q18 27 11 30 Q8 26 10 22 Q14 18 24 17 Z"
          fill="var(--accent)"
          opacity="0.82"
        />
        <path
          d="M24 17 Q30 27 37 30 Q40 26 38 22 Q34 18 24 17 Z"
          fill="var(--accent)"
          opacity="0.82"
        />
      </g>
      {/* Upper (fore) wings — charcoal with ivory moon-spots + soft edges. */}
      <g className="moth-wing moth-wing-upper" style={{ transformOrigin: '24px 17px', transformBox: 'view-box' }}>
        <path
          d="M24 17 Q18 4 9 4 Q3 7 4 13 Q7 17 13 18 Q20 18 24 17 Z"
          fill="currentColor"
        />
        <path
          d="M24 17 Q30 4 39 4 Q45 7 44 13 Q41 17 35 18 Q28 18 24 17 Z"
          fill="currentColor"
        />
        {/* Moon-spots — classic moth markings. Ivory + tiny accent center. */}
        <circle cx="13" cy="10" r="2.3" fill="var(--ivory)" opacity="0.82" />
        <circle cx="35" cy="10" r="2.3" fill="var(--ivory)" opacity="0.82" />
        <circle cx="13" cy="10" r="0.8" fill="var(--accent)" />
        <circle cx="35" cy="10" r="0.8" fill="var(--accent)" />
        {/* Edge dots along the wing borders for texture. */}
        <g fill="var(--ivory)" opacity="0.45">
          <circle cx="7" cy="7" r="0.6" />
          <circle cx="41" cy="7" r="0.6" />
          <circle cx="5" cy="12" r="0.6" />
          <circle cx="43" cy="12" r="0.6" />
        </g>
      </g>
      {/* Body — thin fuzzy capsule */}
      <ellipse cx="24" cy="17" rx="2.2" ry="9" fill="currentColor" />
      {/* Subtle body fuzz hint — short strokes at the body edge. */}
      <g stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.35" fill="none">
        <path d="M22 12 L21 11" />
        <path d="M26 12 L27 11" />
        <path d="M22 22 L21 23" />
        <path d="M26 22 L27 23" />
      </g>
      {/* Feathered antennae — zigzag pattern for the combed look. */}
      <g stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none">
        <path d="M23 8 Q21 4 18 1" />
        <path d="M25 8 Q27 4 30 1" />
      </g>
      {/* Tiny head */}
      <circle cx="24" cy="9" r="1.4" fill="currentColor" />
    </svg>
  );
}

function GlowwormSprite() {
  // Segmented worm with rhythmic glow along its length. Each segment is a
  // separate ellipse with its own CSS animation phase so the body undulates
  // (slight Y-offset wave moving along the body). Head has dot eyes.
  return (
    <svg width="46" height="22" viewBox="0 0 46 22" aria-hidden>
      <GroundShadow cx={23} cy={18.5} rx={18} ry={1.4} />
      <defs>
        <radialGradient id="gw-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.65" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Big glow halo around the whole body */}
      <ellipse cx="23" cy="11" rx="20" ry="9" fill="url(#gw-glow)" />
      {/* Body — 7 segments, alternating charcoal and accent. Each segment
          gets a CSS class so they can bob independently. Segments overlap
          slightly for a smooth undulating worm shape. */}
      <g>
        <circle className="gw-seg gw-seg-0" cx="6" cy="11" r="3.8" fill="currentColor" />
        <circle className="gw-seg gw-seg-1" cx="11" cy="11" r="4.2" fill="var(--accent)" />
        <circle className="gw-seg gw-seg-2" cx="16" cy="11" r="4.4" fill="currentColor" opacity="0.85" />
        <circle className="gw-seg gw-seg-3" cx="22" cy="11" r="4.6" fill="var(--accent)" />
        <circle className="gw-seg gw-seg-4" cx="28" cy="11" r="4.4" fill="currentColor" opacity="0.85" />
        <circle className="gw-seg gw-seg-5" cx="34" cy="11" r="4.2" fill="var(--accent)" />
        <circle className="gw-seg gw-seg-6" cx="40" cy="11" r="3.5" fill="currentColor" />
      </g>
      {/* Head — slightly larger, leading the body. Eyes in ivory with dark
          pupils so it reads as "face" when it looks at you. */}
      <g>
        <circle cx="42" cy="11" r="3" fill="currentColor" />
        <circle cx="43" cy="10" r="0.9" fill="var(--ivory)" />
        <circle cx="43.3" cy="9.8" r="0.3" fill="currentColor" />
      </g>
      {/* Bright ivory spark along the spine — the "glow" highlight. */}
      <g fill="var(--ivory)" opacity="0.65">
        <circle cx="22" cy="11" r="0.9" />
        <circle cx="11" cy="11" r="0.6" />
        <circle cx="34" cy="11" r="0.6" />
      </g>
      {/* Tiny forward feelers */}
      <g stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" fill="none">
        <path d="M44.5 9.5 Q45.5 7 45 5" />
        <path d="M44.5 12.5 Q45.5 15 45 17" />
      </g>
    </svg>
  );
}

function LeafSprite() {
  // A drifting leaf (Aurora = autumn). Realistic leaf shape with pointed
  // tip, rounded base, a prominent midrib and lateral veins. Stem visible.
  // The whole sprite rotates gently via CSS so it reads as "drifting," not
  // static. Accent color on the body, charcoal stem/veins.
  return (
    <svg width="34" height="38" viewBox="0 0 34 38" aria-hidden>
      <GroundShadow cx={17} cy={36} rx={10} ry={1} />
      {/* Leaf blade — asymmetric bezier curves for an organic shape. */}
      <path
        d="M17 3
           Q27 5 29 16
           Q30 25 22 31
           Q17 33 13 31
           Q4 25 5 16
           Q8 5 17 3 Z"
        fill="var(--accent)"
      />
      {/* Subtle highlight on one half for layered paper-cut depth. */}
      <path
        d="M17 4
           Q23 5 26 14
           Q26 20 22 24
           Q18 26 15 22
           Q13 16 15 8
           Q16 5 17 4 Z"
        fill="var(--ivory)"
        opacity="0.18"
      />
      {/* Stem */}
      <path d="M17 31 L17 36" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Midrib and lateral veins — hand-crafted bezier curves that fan out. */}
      <g stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.55">
        <path d="M17 6 Q17 18 17 29" />
        <path d="M17 10 Q12 11 9 13" />
        <path d="M17 10 Q22 11 25 13" />
        <path d="M17 16 Q11 17 7 19" />
        <path d="M17 16 Q23 17 27 19" />
        <path d="M17 22 Q13 23 10 24" />
        <path d="M17 22 Q21 23 24 24" />
        <path d="M17 27 Q14 28 12 28" />
        <path d="M17 27 Q20 28 22 28" />
      </g>
    </svg>
  );
}
